import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, Lock, Key, Eye, FileCheck, Server,
  CheckCircle, ChevronRight, Database, Users, AlertTriangle,
  Clock, Building2, ExternalLink, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const securityPrinciples = [
  {
    icon: Lock,
    title: 'Zero Trust Architecture',
    description: 'Every request is verified. No implicit trust based on network location or previous authentication.'
  },
  {
    icon: Key,
    title: 'Least Privilege Access',
    description: 'Users and systems receive only the minimum permissions required to perform their functions.'
  },
  {
    icon: Eye,
    title: 'Defense in Depth',
    description: 'Multiple layers of security controls protect against single points of failure.'
  },
  {
    icon: Shield,
    title: 'Security by Design',
    description: 'Security is built into every feature from the start, not bolted on as an afterthought.'
  }
];

const complianceCertifications = [
  {
    name: 'SOC 2 Type II',
    status: 'Certified',
    description: 'Annual audit covering security, availability, and confidentiality controls.',
    icon: FileCheck
  },
  {
    name: 'ISO 27001',
    status: 'Certified',
    description: 'International standard for information security management systems.',
    icon: Building2
  },
  {
    name: 'GDPR',
    status: 'Compliant',
    description: 'Full compliance with EU data protection regulations including DPA availability.',
    icon: Users
  },
  {
    name: 'HIPAA',
    status: 'Available',
    description: 'BAA available for healthcare customers handling protected health information.',
    icon: Shield
  }
];

const dataSecurity = [
  {
    title: 'Encryption at Rest',
    description: 'All data encrypted using AES-256. Customer-managed keys available for Enterprise plans.',
    details: ['AES-256 encryption', 'Customer-managed keys (BYOK)', 'Hardware security modules']
  },
  {
    title: 'Encryption in Transit',
    description: 'All communications encrypted using TLS 1.3. Certificate pinning for mobile apps.',
    details: ['TLS 1.3 minimum', 'Perfect forward secrecy', 'Certificate transparency']
  },
  {
    title: 'Data Isolation',
    description: 'Strict tenant isolation at database, compute, and network layers.',
    details: ['Logical tenant separation', 'Dedicated instances available', 'Network segmentation']
  },
  {
    title: 'Data Residency',
    description: 'Choose where your data is stored. Available regions: US, EU, APAC.',
    details: ['US (us-east, us-west)', 'EU (eu-central, eu-west)', 'APAC (ap-southeast)']
  }
];

const accessControls = [
  {
    title: 'Single Sign-On (SSO)',
    description: 'SAML 2.0 and OIDC support for all major identity providers.',
    providers: ['Okta', 'Azure AD', 'Google Workspace', 'OneLogin', 'PingIdentity']
  },
  {
    title: 'Multi-Factor Authentication',
    description: 'Enforce MFA for all users. Support for TOTP, WebAuthn, and push notifications.',
    providers: ['Authenticator apps', 'Hardware keys (YubiKey)', 'Push notifications']
  },
  {
    title: 'Role-Based Access Control',
    description: 'Granular permissions with predefined roles and custom role support.',
    providers: ['Admin', 'Operator', 'Viewer', 'Custom roles']
  },
  {
    title: 'Session Management',
    description: 'Configurable session timeouts, concurrent session limits, and forced logout.',
    providers: ['Idle timeout', 'Absolute timeout', 'Session revocation']
  }
];

const infrastructureSecurity = [
  {
    title: 'Cloud Infrastructure',
    description: 'Hosted on AWS with SOC 2 compliant data centers. Multi-AZ deployment for high availability.',
    icon: Server
  },
  {
    title: 'Network Security',
    description: 'WAF, DDoS protection, and network segmentation. All traffic monitored and logged.',
    icon: Shield
  },
  {
    title: 'Vulnerability Management',
    description: 'Regular penetration testing, automated vulnerability scanning, and bug bounty program.',
    icon: AlertTriangle
  },
  {
    title: 'Incident Response',
    description: '24/7 security monitoring with defined incident response procedures and SLAs.',
    icon: Clock
  }
];

const auditFeatures = [
  {
    title: 'Comprehensive Audit Logs',
    description: 'Every action logged with user, timestamp, IP, and affected resources. Logs retained for configurable periods.'
  },
  {
    title: 'Log Export & SIEM Integration',
    description: 'Export logs to your SIEM (Splunk, Datadog, Sumo Logic) or object storage for long-term retention.'
  },
  {
    title: 'Evidence Packs',
    description: 'Automated evidence collection for each case including decision rationale, approvals, and execution logs.'
  },
  {
    title: 'Access Reviews',
    description: 'Scheduled access certification campaigns with manager attestation and automatic revocation.'
  }
];

const trustResources = [
  {
    title: 'Security Whitepaper',
    description: 'Detailed overview of our security architecture and controls.',
    type: 'PDF',
    size: '2.4 MB'
  },
  {
    title: 'SOC 2 Type II Report',
    description: 'Available under NDA for qualified prospects.',
    type: 'PDF',
    size: 'On request'
  },
  {
    title: 'Penetration Test Summary',
    description: 'Executive summary of latest third-party penetration test.',
    type: 'PDF',
    size: 'On request'
  },
  {
    title: 'Data Processing Agreement',
    description: 'Standard DPA for GDPR compliance.',
    type: 'PDF',
    size: '156 KB'
  }
];

export const SecurityTrust = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Security & Trust</span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  Security & Trust Center
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
                  Enterprise-grade security built into every layer. Your data is protected by
                  industry-leading controls, continuous monitoring, and rigorous compliance programs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Security Principles */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Security Principles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {securityPrinciples.map((principle, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <principle.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{principle.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance & Certifications */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Compliance & Certifications</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {complianceCertifications.map((cert, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <cert.icon className="w-6 h-6 text-primary" />
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                      cert.status === 'Certified' ? 'bg-green-100 text-green-700' :
                      cert.status === 'Compliant' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {cert.status}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{cert.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Database className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Data Security</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {dataSecurity.map((item, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.description}</p>
                  <ul className="space-y-1">
                    {item.details.map((detail, didx) => (
                      <li key={didx} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Access Controls */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Key className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Access Controls</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {accessControls.map((control, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{control.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{control.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {control.providers.map((provider, pidx) => (
                      <span key={pidx} className="inline-flex items-center px-2 py-1 bg-white text-xs text-foreground rounded-md border border-border">
                        {provider}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Infrastructure Security */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Server className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Infrastructure Security</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {infrastructureSecurity.map((item, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audit & Compliance */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <FileCheck className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Audit & Compliance Features</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {auditFeatures.map((feature, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Resources */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Trust Resources</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trustResources.map((resource, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{resource.type}</span>
                    <span className="text-xs text-muted-foreground">{resource.size}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">{resource.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{resource.description}</p>
                  <Button variant="outline" size="sm" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    {resource.size === 'On request' ? 'Request Access' : 'Download'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Security */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Questions About Security?</h2>
            <p className="text-muted-foreground mb-8">
              Our security team is available to answer questions, provide documentation,
              and support your security review process.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Talk to Security Team
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="mailto:security@vedaya.ai">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  security@vedaya.ai
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default SecurityTrust;
